import React, { createContext, ReactElement, ReactNode, useContext, useMemo } from 'react'
import { useLocalStorage } from 'react-use'

import { useBookkeepingBankAccounts } from '@features/accounts/hooks/useBookkeepingBankAccounts'
import { useFetchBankAccounts } from '@features/bankAccounts/hooks/useFetchBankAccounts'
import { BankAccount } from '@features/bankAccounts/types/bankAccount'
import { useOpenBankingAccounts } from '@features/openBanking/hooks/useOpenBankingAccounts'
import { OpenBankingAccount } from '@features/openBanking/types/openBankingAccount'
import { Account } from '@modules-deprecated/app/accounts/types'

import { LocalStorageKeys } from '../../../enums/localStorageKeys'
import { findWidgetBankConnection } from '../widgets/BankIntegration/utils/findWidgetBankConnection'
import { getConnectedBookkeepingAccounts } from '../widgets/BankIntegration/utils/getConnectedBookkeepingAccounts'

interface ContextState {
  isLoadingAccounts: boolean
  selectedBankAccountId: string | undefined
  setSelectedBankAccountId: (accountId: string) => void
  selectedConnectedAccount?: OpenBankingAccount | BankAccount
  connectedBookkeepingAccounts?: Account[]
  unConnectedBankAccounts?: Account[]
}

const BankWidgetContext = createContext<ContextState | undefined>(undefined)

interface BankWidgetContextProviderProps {
  organizationId: string
  children?: ReactNode
}

export const BankWidgetContextProvider = ({
  children,
  organizationId,
}: BankWidgetContextProviderProps): ReactElement => {
  const { bookkeepingBankAccounts: allAccounts = [], isLoading: isAllAccountsLoading } = useBookkeepingBankAccounts()
  const { openBankingAccounts, isLoading: isLoadingOpenBankingAccounts } = useOpenBankingAccounts()
  const { bankAccounts, isLoading: isLoadingBankAccounts } = useFetchBankAccounts()

  const localStorageKey = `${LocalStorageKeys.BankIntegrationSelection}-${organizationId}`

  const [selectedBankAccountId, setSelectedBankAccountId] = useLocalStorage(localStorageKey, '')

  const connectedBookkeepingAccounts: Account[] = useMemo(
    () => getConnectedBookkeepingAccounts(allAccounts, openBankingAccounts, bankAccounts),
    [allAccounts, openBankingAccounts, bankAccounts],
  )

  const unConnectedBankAccounts = useMemo(
    () =>
      allAccounts.filter(
        (account) =>
          !account.isArchived &&
          !connectedBookkeepingAccounts?.some((connectedAccount) => connectedAccount.id === account.id),
      ),
    [allAccounts, connectedBookkeepingAccounts],
  )
  const selectedConnectedAccount = useMemo(
    () =>
      findWidgetBankConnection({
        openBankingAccounts,
        bankAccounts,
        selectedBankAccountId,
        connectedBookkeepingAccounts,
      }) || undefined,
    [openBankingAccounts, bankAccounts, selectedBankAccountId, connectedBookkeepingAccounts],
  )

  const isLoadingAccounts = isAllAccountsLoading || isLoadingBankAccounts || isLoadingOpenBankingAccounts

  return (
    <BankWidgetContext.Provider
      value={{
        isLoadingAccounts,
        selectedBankAccountId,
        setSelectedBankAccountId,
        unConnectedBankAccounts,
        selectedConnectedAccount,
        connectedBookkeepingAccounts,
      }}
    >
      {children}
    </BankWidgetContext.Provider>
  )
}

export const useBankWidget = () => {
  const context = useContext(BankWidgetContext)

  if (!context) {
    throw new Error('BankWidgetContextProvider is missing in the module!')
  }

  return context
}
